import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { NAV } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import {
  Inbox, Mail, Phone, MessageSquareText, ArrowUpRight, Trash2, CheckCircle2, ExternalLink, Send, AlertTriangle,
} from "lucide-react";

const STATUS_STYLE = {
  new: { bg: "#DBEAFE", fg: "#1E40AF", label: "New" },
  contacted: { bg: "#FEF3C7", fg: "#92400E", label: "Contacted" },
  promoted: { bg: "#DCFCE7", fg: "#166534", label: "Promoted" },
  ignored: { bg: "#F3F4F6", fg: "#6B7280", label: "Ignored" },
};

export default function PublicLeads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/public-leads");
      setRows(data.leads || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await api.patch(`/public-leads/${id}`, { status });
      setRows((r) => r.map((x) => (x.id === id ? data : x)));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const promote = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/public-leads/${id}/promote`);
      toast.success(data.already_promoted ? "Already promoted — opening lead" : "Promoted to lead");
      await load();
      navigate(`/leads/${data.lead.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    setBusyId(id);
    try {
      await api.delete(`/public-leads/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const resendTeaser = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/public-leads/${id}/resend-teaser`);
      if (data.email_sent) {
        toast.success(`Teaser re-sent to ${data.email} (send #${data.send_count}).`);
      } else {
        toast.error(`Send failed: ${data.email_error || "unknown"}`);
      }
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const stats = {
    total: rows.length,
    new: rows.filter((r) => r.status === "new").length,
    promoted: rows.filter((r) => r.status === "promoted").length,
  };

  return (
    <div data-testid="public-leads-container" className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-8 flex items-start justify-between gap-6">
        <div>
          <div className="overline mb-2">Back office</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Inbound submissions</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Everyone who filled out the contact form on your public landing page. Promote to lead to run outreach & website generation.
          </p>
        </div>
        <Link to="/" target="_blank" className="lf-btn-outline flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> View landing page
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8 max-w-xl">
        <Stat label="Total" value={stats.total} />
        <Stat label="Awaiting reply" value={stats.new} accent />
        <Stat label="Promoted" value={stats.promoted} />
      </div>

      {loading ? (
        <div className="text-neutral-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-neutral-300 bg-white p-16 text-center">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
          <div className="font-display text-xl mb-2">No inbound submissions yet.</div>
          <p className="text-neutral-500 text-sm">
            When someone fills out your <Link to="/" className="border-b border-black">landing page contact form</Link>, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const s = STATUS_STYLE[r.status] || STATUS_STYLE.new;
            const isBusy = busyId === r.id;
            return (
              <div
                key={r.id}
                data-testid={`public-lead-${r.id}`}
                className="bg-white border border-neutral-200 hover:border-neutral-400 transition p-5"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-display text-lg font-bold">{r.name}</span>
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs font-semibold uppercase tracking-widest"
                        style={{ background: s.bg, color: s.fg }}
                      >
                        {s.label}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-500 flex flex-wrap gap-x-4 gap-y-1">
                      {r.business && <span>{r.business}</span>}
                      {r.industry && <span>{r.industry}</span>}
                      <span className="font-mono text-xs">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {r.source === "audit_lead_magnet" && (
                      <button
                        data-testid={`public-lead-resend-teaser-${r.id}`}
                        onClick={() => resendTeaser(r.id)}
                        disabled={isBusy}
                        className="lf-btn-outline text-xs px-3 py-1.5 flex items-center gap-1"
                        title={r.teaser_email_error ? `Last error: ${r.teaser_email_error}` : "Resend the audit teaser PDF"}
                      >
                        {r.teaser_email_error ? (
                          <AlertTriangle className="w-3 h-3 text-amber-600" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        {isBusy ? "Sending…" : (r.teaser_send_count > 0 ? `Resend teaser (${r.teaser_send_count})` : "Send teaser")}
                      </button>
                    )}
                    {r.status !== "promoted" && (
                      <>
                        {r.status === "new" && (
                          <button
                            onClick={() => setStatus(r.id, "contacted")}
                            disabled={isBusy}
                            className="lf-btn-outline text-xs px-3 py-1.5 flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3 h-3" /> Mark contacted
                          </button>
                        )}
                        <button
                          data-testid={`public-lead-promote-${r.id}`}
                          onClick={() => promote(r.id)}
                          disabled={isBusy}
                          className="lf-btn-primary text-xs px-3 py-1.5 flex items-center gap-1"
                        >
                          <ArrowUpRight className="w-3 h-3" />
                          {isBusy ? "Promoting…" : "Promote to lead"}
                        </button>
                      </>
                    )}
                    {r.status === "promoted" && r.promoted_lead_id && (
                      <Link
                        to={`/leads/${r.promoted_lead_id}`}
                        className="lf-btn-outline text-xs px-3 py-1.5 flex items-center gap-1"
                      >
                        Open lead <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                    <button
                      onClick={() => remove(r.id)}
                      disabled={isBusy}
                      className="p-1.5 text-neutral-400 hover:text-red-600 transition"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mb-3">
                  {r.email && (
                    <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-neutral-600 hover:text-black">
                      <Mail className="w-3 h-3" /> {r.email}
                    </a>
                  )}
                  {r.phone && (
                    <a href={`tel:${r.phone}`} className="flex items-center gap-2 text-neutral-600 hover:text-black">
                      <Phone className="w-3 h-3" /> {r.phone}
                    </a>
                  )}
                </div>
                {r.message && (
                  <div className="border-l-2 border-neutral-200 pl-3 py-1 flex gap-2 text-sm text-neutral-700">
                    <MessageSquareText className="w-3 h-3 text-neutral-400 shrink-0 mt-1" />
                    <span className="whitespace-pre-wrap">{r.message}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-neutral-200 p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold ${accent ? "text-[#002FA7]" : ""}`}>{value}</div>
    </div>
  );
}
